import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxItem,
  ComboboxSearchField,
  ComboboxTrigger,
  ComboboxTriggerLoading,
  ComboboxValue,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  InputMask,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectTriggerLoading,
  SelectValue,
} from '@barfacil/barfacil-ui'
import { useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { TaxList } from '@/api/_schemas/management'
import { FormWithTitleContainer } from '@/app/panel/management/price-list/_components/form-with-title-container'
import { useUpdateProductContext } from '@/app/panel/management/price-list/_contexts/update-product-context'
import { useProductInfo } from '@/app/panel/management/price-list/_hooks/use-product-info'
import { useSyncTaxGroup } from '@/app/panel/management/price-list/_hooks/use-sync-tax-group'
import { useTaxGroup } from '@/app/panel/management/price-list/_hooks/use-tax-group'
import { useTaxList } from '@/app/panel/management/price-list/_hooks/use-tax-list'
import { FormUpdateProduct } from '@/app/panel/management/price-list/_schemas/update-product'
import { removeAccents, slugify } from '@/lib/utils'

const IcmsFields = () => {
  const { open } = useUpdateProductContext()

  const { data: icmsOrigemList, isLoading: icmsOrigemListLoading } = useTaxList(
    'icmsOrigemList',
    open,
  )
  const { data: icmsSituacaoList, isLoading: icmsSituacaoListLoading } =
    useTaxList('icmsSituacaoList', open)
  const { data: icmsModalidadeList, isLoading: icmsModalidadeListLoading } =
    useTaxList('icmsModalidadeList', open)

  const form = useFormContext<FormUpdateProduct>()
  const icmsSituacaoTributaria = form.watch('icms_situacao_tributaria') ?? ''

  return (
    <FormWithTitleContainer title="ICMS">
      <FormField
        control={form.control}
        name="icms_situacao_tributaria"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Situação tributária</FormLabel>

            <Select
              onValueChange={field.onChange}
              defaultValue={field.value || ''}
            >
              <FormControl>
                {icmsSituacaoListLoading ? (
                  <SelectTriggerLoading />
                ) : (
                  <SelectTrigger disabled={form.formState.isSubmitting}>
                    {icmsSituacaoList?.find(({ id }) => id === field.value)
                      ?.text ?? 'Selecione a situação tributária'}
                  </SelectTrigger>
                )}
              </FormControl>

              <SelectContent>
                <SelectGroup>
                  {icmsSituacaoList?.map(({ id, text }) => (
                    <SelectItem className="flex gap-1" key={id} value={id}>
                      {text}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="icms_origem"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Origem</FormLabel>

            <Select
              onValueChange={field.onChange}
              defaultValue={field.value || ''}
            >
              <FormControl>
                {icmsOrigemListLoading ? (
                  <SelectTriggerLoading />
                ) : (
                  <SelectTrigger disabled={form.formState.isSubmitting}>
                    {icmsOrigemList?.find(({ id }) => id === field.value)
                      ?.text ?? 'Selecione a origem'}
                  </SelectTrigger>
                )}
              </FormControl>

              <SelectContent>
                <SelectGroup>
                  {icmsOrigemList?.map(({ id, text }) => (
                    <SelectItem className="flex gap-1" key={id} value={id}>
                      {text}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <FormMessage />
          </FormItem>
        )}
      />

      {['00', '60'].includes(icmsSituacaoTributaria) && (
        <FormField
          control={form.control}
          name="icms_aliquota"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Alíquota {icmsSituacaoTributaria === '60' && 'ICMS Efetivo'}
              </FormLabel>

              <FormControl>
                <InputMask
                  {...field}
                  value={field.value || ''}
                  mask="percentage"
                  disabled={form.formState.isSubmitting}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {['60', '500', '900'].includes(icmsSituacaoTributaria) && (
        <FormField
          control={form.control}
          name="cest"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CEST</FormLabel>

              <FormControl>
                <Input
                  autoFocus
                  placeholder="CEST"
                  {...field}
                  value={field.value || ''}
                  maxLength={7}
                  disabled={form.formState.isSubmitting}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {icmsSituacaoTributaria === '60' && (
        <FormField
          control={form.control}
          name="icms_percentual_reducao_base_calculo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Redução BC Efetivo</FormLabel>

              <FormControl>
                <InputMask
                  {...field}
                  value={field.value || ''}
                  mask="percentage"
                  disabled={form.formState.isSubmitting}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
      )}

      {icmsSituacaoTributaria === '00' && (
        <FormField
          control={form.control}
          name="icms_modalidade_base_calculo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Modalidade da base de cálculo</FormLabel>

              <Select
                onValueChange={field.onChange}
                defaultValue={field.value || ''}
              >
                <FormControl>
                  {icmsModalidadeListLoading ? (
                    <SelectTriggerLoading />
                  ) : (
                    <SelectTrigger disabled={form.formState.isSubmitting}>
                      <SelectValue placeholder="Selecione a modalidade da base de cálculo" />
                    </SelectTrigger>
                  )}
                </FormControl>

                <SelectContent>
                  <SelectGroup>
                    {icmsModalidadeList?.map(({ id, text }) => (
                      <SelectItem className="flex gap-1" key={id} value={id}>
                        {text}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </FormWithTitleContainer>
  )
}

const PisCofinsFields = () => {
  const [openComboboxPis, setOpenComboboxPis] = useState(false)
  const [openComboboxCofins, setOpenComboboxCofins] = useState(false)

  const [pisSearch, setPisSearch] = useState('')
  const [cofinsSearch, setCofinsSearch] = useState('')

  const [filteredPisList, setFilteredPisList] = useState<TaxList>([])
  const [filteredCofinsList, setFilteredCofinsList] = useState<TaxList>([])

  const { open } = useUpdateProductContext()

  const { data: pisCofinsTipoList, isLoading: pisCofinsTipoListLoading } =
    useTaxList('pisCofinsTipoList', open)
  const {
    data: pisCofinsSituacaoList,
    isLoading: pisCofinsSituacaoListLoading,
  } = useTaxList('pisCofinsSituacaoList', open)

  const form = useFormContext<FormUpdateProduct>()
  const pisST = form.watch('pis_situacao_tributaria') ?? ''
  const cofinsST = form.watch('cofins_situacao_tributaria') ?? ''
  const productId = form.watch('cod_produto') ?? ''

  const { isLoading: isLoadingProductWithTax } = useProductInfo(
    {
      id: productId,
    },
    open,
  )

  function handlePisSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const value = event.currentTarget.value

    setPisSearch(value)
    setFilteredPisList(
      pisCofinsSituacaoList?.filter(({ text }) =>
        removeAccents(text.toUpperCase()).includes(
          removeAccents(value.toUpperCase()),
        ),
      ) ?? [],
    )
  }

  function handleCofinsSearchChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const value = event.currentTarget.value

    setCofinsSearch(value)
    setFilteredCofinsList(
      pisCofinsSituacaoList?.filter(({ text }) =>
        removeAccents(text.toUpperCase()).includes(
          removeAccents(value.toUpperCase()),
        ),
      ) ?? [],
    )
  }

  return (
    <>
      <FormWithTitleContainer title="PIS">
        <FormField
          control={form.control}
          name="pis_situacao_tributaria"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Situação tributária</FormLabel>

              <Combobox
                open={openComboboxPis}
                onOpenChange={setOpenComboboxPis}
                onValueChange={field.onChange}
                defaultValue={field.value || ''}
                focusSelected={false}
              >
                <FormControl>
                  {pisCofinsSituacaoListLoading ? (
                    <ComboboxTriggerLoading>
                      Carregando...
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger
                      onBlur={() => setPisSearch('')}
                      disabled={
                        form.formState.isSubmitting || isLoadingProductWithTax
                      }
                    >
                      <ComboboxValue placeholder="Informe a situação tributária">
                        {
                          pisCofinsSituacaoList?.find(
                            ({ id }) => id === field.value,
                          )?.text
                        }
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={pisSearch}
                    autoComplete="off"
                    loading={pisCofinsSituacaoListLoading}
                    onChange={handlePisSearchChange}
                  />

                  <ComboboxGroup>
                    {(!pisSearch
                      ? pisCofinsSituacaoList
                      : filteredPisList
                    )?.map(({ id, text }) => (
                      <ComboboxItem
                        key={id}
                        value={id}
                        onClick={() => {
                          form.setValue('pis_situacao_tributaria', id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                          setOpenComboboxPis(false)
                        }}
                      >
                        {text}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>

              <FormMessage />
            </FormItem>
          )}
        />

        {['01', '02'].includes(pisST) && (
          <>
            <FormField
              control={form.control}
              name="pis_tipo_tributacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de tributação</FormLabel>

                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value || ''}
                  >
                    <FormControl>
                      {pisCofinsTipoListLoading ? (
                        <SelectTriggerLoading />
                      ) : (
                        <SelectTrigger disabled={form.formState.isSubmitting}>
                          {pisCofinsTipoList?.find(
                            ({ id }) => id === field.value,
                          )?.text ?? 'Selecione o tipo de tributação'}
                        </SelectTrigger>
                      )}
                    </FormControl>

                    <SelectContent>
                      <SelectGroup>
                        {pisCofinsTipoList?.map(({ id, text }) => (
                          <SelectItem
                            className="flex gap-1"
                            key={id}
                            value={id}
                          >
                            {text}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pis_aliquota_percentual"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Alíquota</FormLabel>

                  <FormControl>
                    <InputMask
                      {...field}
                      value={field.value || ''}
                      mask="percentage"
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
      </FormWithTitleContainer>

      <FormWithTitleContainer title="COFINS">
        <FormField
          control={form.control}
          name="cofins_situacao_tributaria"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Situação tributária</FormLabel>

              <Combobox
                open={openComboboxCofins}
                onOpenChange={setOpenComboboxCofins}
                onValueChange={field.onChange}
                defaultValue={field.value || ''}
                focusSelected={false}
              >
                <FormControl>
                  {pisCofinsSituacaoListLoading ? (
                    <ComboboxTriggerLoading>
                      Carregando...
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger
                      onBlur={() => setCofinsSearch('')}
                      disabled={
                        form.formState.isSubmitting || isLoadingProductWithTax
                      }
                    >
                      <ComboboxValue placeholder="Informe a situação tributária">
                        {
                          pisCofinsSituacaoList?.find(
                            ({ id }) => id === field.value,
                          )?.text
                        }
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={cofinsSearch}
                    autoComplete="off"
                    loading={pisCofinsSituacaoListLoading}
                    onChange={handleCofinsSearchChange}
                  />

                  <ComboboxGroup>
                    {(!cofinsSearch
                      ? pisCofinsSituacaoList
                      : filteredCofinsList
                    )?.map(({ id, text }) => (
                      <ComboboxItem
                        key={id}
                        value={id}
                        onClick={() => {
                          form.setValue('cofins_situacao_tributaria', id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                          setOpenComboboxCofins(false)
                        }}
                      >
                        {text}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>

              <FormMessage />
            </FormItem>
          )}
        />

        {['01', '02'].includes(cofinsST) && (
          <>
            <FormField
              control={form.control}
              name="cofins_tipo_tributacao"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de tributação</FormLabel>

                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value || ''}
                  >
                    <FormControl>
                      {pisCofinsTipoListLoading ? (
                        <SelectTriggerLoading />
                      ) : (
                        <SelectTrigger disabled={form.formState.isSubmitting}>
                          {pisCofinsTipoList?.find(
                            ({ id }) => id === field.value,
                          )?.text ?? 'Selecione o tipo de tributação'}
                        </SelectTrigger>
                      )}
                    </FormControl>

                    <SelectContent>
                      <SelectGroup>
                        {pisCofinsTipoList?.map(({ id, text }) => (
                          <SelectItem
                            className="flex gap-1"
                            key={id}
                            value={id}
                          >
                            {text}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>

                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cofins_aliquota_percentual"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Alíquota</FormLabel>

                  <FormControl>
                    <InputMask
                      {...field}
                      value={field.value || ''}
                      mask="percentage"
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
      </FormWithTitleContainer>
    </>
  )
}

export const UpdateProductTaxForm = () => {
  const { open } = useUpdateProductContext()
  const { syncTax } = useSyncTaxGroup()

  const [openComboboxTaxGroup, setOpenComboboxTaxGroup] = useState(false)
  const [openComboboxCfop, setOpenComboboxCfop] = useState(false)
  const [openComboboxNcm, setOpenComboboxNcm] = useState(false)

  const [taxGroupSearch, setTaxGroupSearch] = useState('')
  const [cfopSearch, setCfopSearch] = useState('')
  const [ncmSearch, setNcmSearch] = useState('')
  const [debounceTaxGroupValue, setDebounceTaxGroupValue] = useState('')
  const [debounceCfopValue, setDebounceCfopValue] = useState('')
  const [debounceNcmValue, setDebounceNcmValue] = useState('')

  const debounceTaxGroupSearch = useRef<number>(0)
  const debounceNcmSearch = useRef<number>(0)
  const debounceCfopSearch = useRef<number>(0)

  const { data: cfopList, isLoading: cfopListLoading } = useTaxList(
    'cfopList',
    open,
    debounceCfopValue,
  )
  const { data: ncmList, isLoading: ncmListLoading } = useTaxList(
    'ncmList',
    open,
    debounceNcmValue,
  )

  const { data: taxGroupList, isLoading: taxGroupListLoading } = useTaxGroup(
    open,
    debounceTaxGroupValue,
  )

  const form = useFormContext<FormUpdateProduct>()

  function handleTaxGroupSearchChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    window.clearInterval(debounceTaxGroupSearch.current)

    const { value } = event.currentTarget

    setTaxGroupSearch(value)
    debounceTaxGroupSearch.current = window.setTimeout(() => {
      return setDebounceTaxGroupValue(value)
    }, 550)
  }

  function handleCfopSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    window.clearInterval(debounceCfopSearch.current)

    const { value } = event.currentTarget

    setCfopSearch(value)
    debounceCfopSearch.current = window.setTimeout(() => {
      return setDebounceCfopValue(value)
    }, 550)
  }

  function handleNcmSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    window.clearInterval(debounceNcmSearch.current)

    const { value } = event.currentTarget

    setNcmSearch(value)
    debounceNcmSearch.current = window.setTimeout(() => {
      return setDebounceNcmValue(value)
    }, 550)
  }

  function handleOnSelectTaxGroup(id: string) {
    form.setValue('grupo_tributario', id, {
      shouldDirty: true,
      shouldValidate: true,
    })

    syncTax(
      form,
      taxGroupList?.find((item) => item.cod_grupo_tributario === Number(id)),
    )

    setOpenComboboxTaxGroup(false)
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-2">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-2 lg:grid-cols-3">
        <FormField
          control={form.control}
          name="cod_produto"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Código</FormLabel>

              <FormControl>
                <Input {...field} disabled />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cod_externo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Código externo</FormLabel>

              <FormControl>
                <Input
                  autoFocus
                  placeholder="Código externo"
                  {...field}
                  value={field.value || ''}
                  disabled={form.formState.isSubmitting}
                  onChange={(e) => field.onChange(slugify(e.target.value))}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="unidade_medida"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Unidade de medida</FormLabel>

              <Select
                onValueChange={field.onChange}
                defaultValue={field.value || ''}
              >
                <FormControl>
                  <SelectTrigger disabled={form.formState.isSubmitting}>
                    <SelectValue placeholder="Selecione a unidade de medida" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent>
                  <SelectGroup>
                    <SelectItem className="flex gap-1" value="UN">
                      Unidade (UN)
                    </SelectItem>

                    <SelectItem className="flex gap-1" value="CX">
                      Caixa (CX)
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="grupo_tributario"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Grupo tributário</FormLabel>

              <Combobox
                open={openComboboxTaxGroup}
                onOpenChange={setOpenComboboxTaxGroup}
                onValueChange={field.onChange}
                defaultValue={field.value || ''}
                focusSelected={false}
              >
                <FormControl>
                  {taxGroupListLoading ? (
                    <ComboboxTriggerLoading>
                      Carregando...
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger
                      onBlur={() => setTaxGroupSearch('')}
                      disabled={form.formState.isSubmitting}
                    >
                      <ComboboxValue placeholder="Selecione o grupo tributário">
                        {
                          taxGroupList?.find(
                            ({ cod_grupo_tributario: id }) =>
                              id === Number(field.value),
                          )?.descricao
                        }
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={taxGroupSearch}
                    autoComplete="off"
                    loading={taxGroupListLoading}
                    onChange={handleTaxGroupSearchChange}
                  />

                  <ComboboxGroup>
                    {taxGroupList?.map(
                      ({ cod_grupo_tributario: id, descricao: text }) => (
                        <ComboboxItem
                          key={id}
                          value={id.toString()}
                          onClick={() => handleOnSelectTaxGroup(id.toString())}
                        >
                          {text}
                        </ComboboxItem>
                      ),
                    )}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cfop"
          render={({ field }) => (
            <FormItem>
              <FormLabel>CFOP</FormLabel>

              <Combobox
                open={openComboboxCfop}
                onOpenChange={setOpenComboboxCfop}
                onValueChange={field.onChange}
                defaultValue={field.value || ''}
                focusSelected={false}
              >
                <FormControl>
                  {cfopListLoading ? (
                    <ComboboxTriggerLoading>
                      Carregando...
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger
                      onBlur={() => setCfopSearch('')}
                      disabled={form.formState.isSubmitting}
                    >
                      <ComboboxValue placeholder="Informe o CFOP">
                        {cfopList?.find(({ id }) => id === field.value)?.text}
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={cfopSearch}
                    autoComplete="off"
                    loading={cfopListLoading}
                    onChange={handleCfopSearchChange}
                  />

                  <ComboboxGroup>
                    {cfopList?.map(({ id, text }) => (
                      <ComboboxItem
                        key={id}
                        value={id}
                        onClick={() => {
                          form.setValue('cfop', id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                          setOpenComboboxCfop(false)
                        }}
                      >
                        {text}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="ncm"
          render={({ field }) => (
            <FormItem>
              <FormLabel>NCM</FormLabel>

              <Combobox
                open={openComboboxNcm}
                onOpenChange={setOpenComboboxNcm}
                onValueChange={field.onChange}
                defaultValue={field.value || ''}
                focusSelected={false}
              >
                <FormControl>
                  {ncmListLoading ? (
                    <ComboboxTriggerLoading>
                      Carregando...
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger
                      onBlur={() => setNcmSearch('')}
                      disabled={form.formState.isSubmitting}
                    >
                      <ComboboxValue placeholder="Informe o NCM">
                        {ncmList?.find(({ id }) => id === field.value)?.text}
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={ncmSearch}
                    autoComplete="off"
                    loading={ncmListLoading}
                    onChange={handleNcmSearchChange}
                  />

                  <ComboboxGroup>
                    {ncmList?.map(({ id, text }) => (
                      <ComboboxItem
                        key={id}
                        value={id}
                        onClick={() => {
                          form.setValue('ncm', id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                          setOpenComboboxNcm(false)
                        }}
                      >
                        {text}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>

              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <IcmsFields />
      <PisCofinsFields />
    </div>
  )
}
